import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useUnsafeCallback, useUnsafeMemo } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, Download, Pencil, Upload } from '@factorialco/f0-react/icons/app'
import {
  CostCentersListDocument,
  CostCentersListQuery,
  CostCentersValueObjectsCostCenterStatusEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { COST_CENTER_STATUS } from 'modules/cost_centers/lib/consts'
import { trackCostCenterAction } from 'modules/cost_centers/lib/costCenterHelpers'
import { CostCenterDetails } from 'modules/cost_centers/lib/types'
import { ExportSidepanel } from 'modules/universal_exports/components/ExportSidepanel'

const getCostCenterStatus = (status: string) => {
  if (status === COST_CENTER_STATUS.active) return 'positive'
  return 'warning' // Or 'critical'
}

const getCostCenterStatusLabel = (status: CostCentersValueObjectsCostCenterStatusEnum) => {
  if (status === COST_CENTER_STATUS.active)
    return i18n.t('finance.cost_centers_list.filters.status.active')
  if (status === COST_CENTER_STATUS.inactive)
    return i18n.t('finance.cost_centers_list.filters.status.inactive')
  return '-'
}

const CostCentersList = ({ canManageCostCenters }: { canManageCostCenters: boolean }) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { company } = useCurrentSession()

  const [showExportModal, setShowExportModal] = useState(false)

  const addNewCostCenter = useUnsafeCallback(() => {
    track(trackingEvents.accounting.createCostCenterStart, {})
    navigate(navigation.settings.finance.costCenters.list.newCostCenter.fullPath())
  }, [navigate, navigation])

  const handleDeleteAsset = useUnsafeCallback(
    (item: CostCenterDetails) => {
      navigate(navigation.settings.finance.costCenters.list.delete.fullPath({ id: item.id }))
      trackCostCenterAction(trackingEvents.accounting.deleteCostCenterStart, item)
    },
    [navigate, navigation]
  )

  const handleEditAsset = useUnsafeCallback(
    (item: CostCenterDetails) => {
      navigate(navigation.settings.finance.costCenters.list.edit.fullPath({ id: item.id }))
    },
    [navigate, navigation]
  )

  const mapData = useUnsafeCallback((data: CostCentersListQuery) => {
    const edges = data.costCenters.costCentersConnection.edges
    return edges.map(({ node }) => ({ ...node }))
  }, [])

  const dataSourceOptions = useUnsafeMemo(
    () => ({
      document: CostCentersListDocument,
      mapData: (tableData: CostCentersListQuery) => mapData(tableData),
      sortings: {
        name: {
          label: i18n.t('finance.account_list.headers.name'),
          sortBy: 'name',
        },
        code: {
          label: i18n.t('finance.cost_centers_list.headers.code'),
          sortBy: 'code',
        },
      },
      defaultSortings: {
        field: 'name' as const,
        order: 'asc' as const,
      },
      search: {
        enabled: true,
      },
      filters: {
        legalEntityIds: {
          type: 'in' as const,
          label: i18n.t('finance.cost_centers_list.filters.legal_entity'),
          options: {
            options: company.legalEntities.map(({ id, legalName }) => ({
              value: id,
              label: legalName || '',
            })),
          },
        },
        statuses: {
          type: 'in' as const,
          label: i18n.t('finance.cost_centers_list.filters.status.label'),
          options: {
            options: [
              {
                value: 'active' as const,
                label: i18n.t('finance.cost_centers_list.filters.status.active'),
              },
              {
                value: 'inactive' as const,
                label: i18n.t('finance.cost_centers_list.filters.status.inactive'),
              },
            ],
          },
        },
      },
      presets: [
        {
          label: i18n.t('finance.cost_centers_list.filters.status.active'),
          filter: { statuses: ['active' as const] },
        },
        {
          label: i18n.t('finance.cost_centers_list.filters.status.inactive'),
          filter: { statuses: ['inactive' as const] },
        },
      ],
      primaryActions: canManageCostCenters
        ? () => ({
            icon: Add,
            onClick: () => addNewCostCenter(),
            label: i18n.t('finance.buttons.new_cost_center'),
          })
        : undefined,
      secondaryActions: canManageCostCenters
        ? {
            expanded: 1 as const,
            actions: () => [
              {
                icon: Upload,
                onClick: () => {
                  navigate(navigation.settings.finance.costCenters.list.import.fullPath())
                },
                label: i18n.t('finance.cost_centers_list.table_options.upload'),
              },
              {
                icon: Download,
                onClick: () => {
                  setShowExportModal(true)
                },
                label: i18n.t('finance.cost_centers_list.table_options.download'),
              },
            ],
          }
        : undefined,
      itemActions: (item: CostCenterDetails) => {
        const actions = [
          {
            label: i18n.t('finance.cost_centers_list.popover.edit'),
            icon: Pencil,
            onClick: () => {
              handleEditAsset(item)
            },
            critical: false,
          },
        ]
        if (!item.hasAssignments) {
          actions.push({
            label: i18n.t('finance.cost_centers_list.popover.delete'),
            icon: Delete,
            onClick: () => {
              handleDeleteAsset(item)
            },
            critical: true,
          })
        }
        return actions
      },
      mapVariables: ({
        search,
        filters,
        sortings,
      }: {
        search?: string
        sortings?: Array<{ field: string; order: string }>
        filters?: {
          legalEntityIds?: number[]
          statuses?: CostCentersValueObjectsCostCenterStatusEnum[]
        }
      }) => {
        return {
          search: search,
          companyId: company.id,
          sortOrder: sortings?.[0],
          statuses: filters?.statuses,
          legalEntityIds: filters?.legalEntityIds,
        }
      },
      mapPagination: (tableData: CostCentersListQuery) => ({
        total: tableData.costCenters.costCentersConnection.totalCount ?? 0,
      }),
      totalItemSummary: (totalCount: number) =>
        i18n.tp('finance.cost_centers_list.count_cost_centers', { count: totalCount }),
    }),
    [
      company,
      mapData,
      navigate,
      navigation,
      handleEditAsset,
      addNewCostCenter,
      handleDeleteAsset,
      canManageCostCenters,
    ]
  )

  const source = useGraphqlDataCollectionSource(dataSourceOptions, [company])

  return (
    <>
      <OneDataCollection
        source={source}
        emptyStates={{
          'no-data': {
            title: i18n.t('finance.cost_centers_list.empty.title'),
            description: canManageCostCenters
              ? i18n.t('finance.cost_centers_list.empty.description')
              : '',
            actions: canManageCostCenters
              ? [
                  {
                    variant: 'outline' as const,
                    icon: Upload,
                    onClick: () => {
                      navigate(navigation.settings.finance.costCenters.list.import.fullPath())
                    },
                    label: i18n.t('finance.cost_centers_list.table_options.upload'),
                  },
                  {
                    icon: Add,
                    label: i18n.t('finance.buttons.new_cost_center'),
                    onClick: () => addNewCostCenter(),
                  },
                ]
              : undefined,
          },
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  sorting: 'name',
                  label: i18n.t('finance.cost_centers_list.headers.name'),
                  render: (item: CostCenterDetails) => {
                    return {
                      type: 'text',
                      value: item.name,
                    }
                  },
                },
                {
                  sorting: 'code',
                  label: i18n.t('finance.cost_centers_list.headers.code'),
                  render: (item: CostCenterDetails) => {
                    return {
                      type: 'text',
                      value: item.code || '-',
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_centers_list.headers.legal_entity'),
                  render: (item: CostCenterDetails) => {
                    return {
                      type: 'text',
                      value:
                        item.legalEntity?.legalName ||
                        i18n.t('finance.cost_centers_list.legal_entity_unassigned'),
                    }
                  },
                },
                {
                  label: i18n.t('finance.cost_centers_list.headers.status'),
                  render: (item: CostCenterDetails) => {
                    return {
                      type: 'status',
                      value: {
                        status: getCostCenterStatus(item.status),
                        label: getCostCenterStatusLabel(item.status),
                      },
                    }
                  },
                },
              ],
            },
          },
        ]}
      />
      {showExportModal && (
        <ExportSidepanel
          resource="cost_centers.cost_center"
          onClose={() => setShowExportModal(false)}
        />
      )}
    </>
  )
}

export { CostCentersList }
